import { MarkNode, $unwrapMarkNode, $wrapSelectionInMarkNode } from '@lexical/mark';
import {
  $create,
  $getState,
  $isRootOrShadowRoot,
  $setState,
  createState,
  DOMExportOutput,
  LexicalNode,
  RangeSelection,
  SerializedElementNode,
  Spread,
} from 'lexical';
import { Source } from '../types';
import { SUGGESTION_COLORS } from './suggestion-colors';
import { $isEffectivelyEmpty } from './suggestion-utils';

type SuggestionMarkState = {
  source: Source;
  tooltip: string | null;
  focusedAt: number | null;
  markersHidden: boolean;
};

type SerializedSuggestionMarkNode = Spread<
  {
    ids: Array<string>;
  },
  SerializedElementNode
>;

const suggestionMarkState = createState<'suggestion-mark-state', SuggestionMarkState>(
  'suggestion-mark-state',
  {
    parse: () => ({
      source: 'default',
      tooltip: null,
      focusedAt: null,
      markersHidden: false,
    }),
  },
);

export class SuggestionMarkNode extends MarkNode {
  $config() {
    return this.config('suggestion-mark', {
      extends: MarkNode,
      stateConfigs: [{ flat: true, stateConfig: suggestionMarkState }],
    });
  }

  static importDOM(): null {
    return null;
  }

  createDOM(): HTMLElement {
    const element = document.createElement('mark');
    const currentState = $getState(this, suggestionMarkState);
    element.dataset['suggestion'] = 'true';
    element.dataset['suggestionSource'] = currentState.source;
    applySuggestionMarkStyle(element, currentState);
    return element;
  }

  updateDOM(_prevNode: this, dom: HTMLElement): boolean {
    const currentState = $getState(this, suggestionMarkState);
    dom.dataset['suggestionSource'] = currentState.source;
    applySuggestionMarkStyle(dom, currentState);
    return false;
  }

  insertNewAfter(selection: RangeSelection, restoreSelection = true): SuggestionMarkNode {
    const currentState = $getState(this, suggestionMarkState);
    const newMark = $createSuggestionMarkNode(currentState.source, currentState.tooltip);
    for (const id of this.getIDs()) {
      newMark.addID(id);
    }
    this.insertAfter(newMark, restoreSelection);
    return newMark;
  }

  exportDOM(): DOMExportOutput {
    const currentState = $getState(this, suggestionMarkState);
    const element = document.createElement('suggestion');
    element.setAttribute('source', currentState.source);
    if (currentState.tooltip) {
      element.setAttribute('tooltip', currentState.tooltip);
    }
    return { element };
  }

  static importJSON(serializedNode: SerializedSuggestionMarkNode): SuggestionMarkNode {
    return $createSuggestionMarkNode().updateFromJSON(serializedNode) as SuggestionMarkNode;
  }

  exportJSON(): SerializedSuggestionMarkNode {
    return super.exportJSON() as SerializedSuggestionMarkNode;
  }
}

function applySuggestionMarkStyle(element: HTMLElement, state: SuggestionMarkState) {
  element.style.display = 'inline';
  element.style.borderRadius = '4px';

  const bgColor =
    SUGGESTION_COLORS[state.source][state.focusedAt != null ? 'focused' : 'unfocused'];

  element.style.transition = 'background-color 0.4s ease-in-out, padding 0.4s ease-in-out';
  element.style.padding = state.markersHidden ? '0' : '2px 4px';
  element.style.backgroundColor = state.markersHidden ? 'transparent' : bgColor;
  element.style.textDecoration =
    state.source === 'deletion' && !state.markersHidden ? 'line-through' : 'none';

  if (state.tooltip) {
    element.dataset.tooltip = state.tooltip;
  } else {
    delete element.dataset.tooltip;
  }
}

export function $createSuggestionMarkNode(
  source: Source = 'default',
  tooltip: string | null = null,
): SuggestionMarkNode {
  return $setState($create(SuggestionMarkNode), suggestionMarkState, {
    source,
    tooltip,
    focusedAt: null,
    markersHidden: false,
  });
}

export function $isSuggestionMarkNode(
  node: LexicalNode | null | undefined,
): node is SuggestionMarkNode {
  return node instanceof SuggestionMarkNode;
}

export function $getSuggestionMarkState(node: SuggestionMarkNode) {
  return $getState(node, suggestionMarkState);
}

export function $sourceSuggestionMark(node: SuggestionMarkNode): Source {
  return $getState(node, suggestionMarkState).source;
}

export function $tooltipSuggestionMark(node: SuggestionMarkNode): string | null {
  return $getState(node, suggestionMarkState).tooltip;
}

export function $setSuggestionMarkSource(node: SuggestionMarkNode, source: Source) {
  const currentState = $getState(node, suggestionMarkState);
  if (currentState.source !== source) {
    $setState(node, suggestionMarkState, { ...currentState, source });
    node.markDirty();
  }
}

export function $focusSuggestionMark(node: SuggestionMarkNode) {
  const currentState = $getState(node, suggestionMarkState);
  if (currentState.focusedAt != null) return;
  $setState(node, suggestionMarkState, { ...currentState, focusedAt: Date.now() });
}

export function $setMarkersHiddenOnMark(node: SuggestionMarkNode, hidden: boolean) {
  const currentState = $getState(node, suggestionMarkState);
  if (currentState.markersHidden !== hidden) {
    $setState(node, suggestionMarkState, { ...currentState, markersHidden: hidden });
    node.markDirty();
  }
}

export function $dismissSuggestionMark(node: SuggestionMarkNode): void {
  $unwrapMarkNode(node);
}

export function $removeSuggestionMarkWithContent(node: SuggestionMarkNode): void {
  let parent = node.getParent();
  node.remove();

  while (parent && !$isRootOrShadowRoot(parent) && $isEffectivelyEmpty(parent)) {
    const grandparent = parent.getParent();
    parent.remove();
    parent = grandparent;
  }
}

export function $wrapSelectionInSuggestionMark(
  selection: RangeSelection,
  spanId: string,
  source: Source,
  tooltip: string | null = null,
): void {
  $wrapSelectionInMarkNode(selection, false, spanId, (ids: string[]) => {
    const node = $createSuggestionMarkNode(source, tooltip);
    for (const id of ids) {
      node.addID(id);
    }
    return node;
  });
}
